<template>
    <div class="d-flex flex-column h-100 overflow-x-hidden">
        <div class="flex-shrink-0">
            <div class="d-flex align-center pb-4">
                <h2 class="text-h5 font-weight-bold">
                    {{ entryType === 'ware' ? $t('sidebar.wareReservations') : $t('sidebar.truckReservations') }}
                </h2>
                <v-btn
                    icon
                    size="large"
                    variant="text"
                    elevation="0"
                    @click="infoModalVisible = true"
                >
                    <v-icon size="large">mdi-information-outline</v-icon>
                    <v-tooltip activator="parent" location="bottom">
                        {{ $t('reservations.processSteps.title') }}
                    </v-tooltip>
                </v-btn>
            </div>
        </div>
        <div class="reservations-tabs-wrapper flex-grow-1">
            <v-tabs v-model="activeTab">
                <v-tab value="contracted" elevation="0" class="d-flex align-center">
                    {{ $t('reservations.contracted') }}
                </v-tab>
                <v-tab value="received" elevation="0" class="d-flex align-center">
                    {{ $t('reservations.received') }}
                    <ReservationBadge :count="receivedBadgeCount" />
                </v-tab>
                <template #window>
                    <v-tabs-window-item value="contracted">
                        <div class="reservation-tab-content mt-4 px-2 pb-3">
                            <ReservationList
                                :reservations="contractedReservations"
                                :entry-type="entryType"
                                :is-entry-owner="false"
                                :loading="loading"
                                @cancel="openCancelConfirm"
                                @confirm-completed="handleConfirmCompleted"
                                @rated="handleRated"
                                @generate-transport-order="handleGenerateTransportOrder"
                                @download-transport-order="handleDownloadTransportOrder"
                                @modify-transport-order="handleModifyTransportOrder"
                                @send-transport-order="handleSendTransportOrder"
                                @upload-signed-pdf="handleUploadSignedPdf"
                                @upload-cmr="handleUploadCmr"
                                @upload-invoice="handleUploadInvoice"
                                @upload-generated-transport-order="handleUploadGeneratedTransportOrder"
                                @confirm-transport-order="handleConfirmTransportOrder"
                            />
                        </div>
                    </v-tabs-window-item>
                    <v-tabs-window-item value="received">
                        <div class="reservation-tab-content mt-4 px-2 pb-3">
                            <ReservationList
                                :reservations="receivedReservations"
                                :entry-type="entryType"
                                :is-entry-owner="true"
                                :loading="loading"
                                @confirm="handleConfirm"
                                @decline="handleDecline"
                                @cancel="openCancelConfirm"
                                @confirm-completed="handleConfirmCompleted"
                                @rated="handleRated"
                                @generate-transport-order="handleGenerateTransportOrder"
                                @download-transport-order="handleDownloadTransportOrder"
                                @modify-transport-order="handleModifyTransportOrder"
                                @send-transport-order="handleSendTransportOrder"
                                @upload-signed-pdf="handleUploadSignedPdf"
                                @upload-cmr="handleUploadCmr"
                                @upload-invoice="handleUploadInvoice"
                                @upload-generated-transport-order="handleUploadGeneratedTransportOrder"
                                @confirm-transport-order="handleConfirmTransportOrder"
                            />
                        </div>
                    </v-tabs-window-item>
                </template>
            </v-tabs>
        </div>

            <TransportOrderModal
                v-if="transportOrderReservation"
                v-model:visible="transportOrderModalVisible"
                :reservation="transportOrderReservation"
                :entry-type="entryType"
                :field-values="transportOrderFieldValues"
                @save="handleTransportOrderSave"
            />

            <ConfirmDialog
                :visible="showCancelConfirmDialog"
                :title="$t('reservations.confirmCancelTitle')"
                :message="$t('reservations.confirmCancelMessage', { id: cancelReservationToConfirm?.entry_summary?.identification_id || cancelReservationToConfirm?.uid || '' })"
                :primary-text="$t('common.yes')"
                secondary-color="error"
                :secondary-text="$t('common.no')"
                @update:visible="(isVisible) => { if (!isVisible) closeCancelConfirm(); }"
                @primary="confirmCancelReservation"
                @secondary="closeCancelConfirm"
            />

            <v-dialog v-model="infoModalVisible" max-width="600" persistent scrim="black">
                <v-card>
                    <v-card-title class="text-center justify-center">{{ $t('reservations.processSteps.title') }}</v-card-title>
                    <v-card-text class="reservation-info-content">
                        <p class="text-body-2 mb-3" style="white-space: pre-line">{{ $t('reservations.processSteps.intro') }}</p>
                        <p class="text-body-2 mb-3" style="white-space: pre-line">{{ $t('reservations.processSteps.steps') }}</p>
                        <p class="text-body-2" style="white-space: pre-line">{{ entryType === 'ware' ? $t('reservations.processSteps.wareNotes') : $t('reservations.processSteps.truckNotes') }}</p>
                    </v-card-text>
                    <v-card-actions class="justify-center">
                        <v-btn variant="flat" @click="infoModalVisible = false">{{ $t('common.close') }}</v-btn>
                    </v-card-actions>
                </v-card>
            </v-dialog>
    </div>
</template>

<script>
import axios from "axios";
import emitter from "@/eventBus";
import { markReservationTabViewed } from "@/api";
import ConfirmDialog from "@/components/display/ConfirmDialog.vue";
import ReservationBadge from "@/components/ReservationBadge.vue";
import ReservationList from "@/components/reservation/ReservationList.vue";
import TransportOrderModal from "@/components/transport-order/TransportOrderModal.vue";
import { showAlertModal } from "@/utils";

export default {
    components: { ConfirmDialog, ReservationBadge, ReservationList, TransportOrderModal },
    props: {
        entryType: {
            type: String,
            required: true,
            validator: (value) => ["ware", "truck"].includes(value),
        },
    },
    data() {
        return {
            infoModalVisible: false,
            activeTab: "contracted",
            loading: false,
            contractedReservations: [],
            receivedReservations: [],
            transportOrderModalVisible: false,
            transportOrderReservation: null,
            transportOrderFieldValues: null,
            cancelReservationToConfirm: null,
        };
    },
    watch: {
        activeTab() {
            this.fetchReservations();
        },
    },
    computed: {
        receivedBadgeCount() {
            return (this.receivedReservations || []).filter((r) => r.status === "pending").length;
        },
        showCancelConfirmDialog: {
            get() {
                return this.cancelReservationToConfirm !== null;
            },
            set(isVisible) {
                if (!isVisible) this.cancelReservationToConfirm = null;
            },
        },
    },
    async mounted() {
        const raw = this.$route.query.reservation;
        if (raw != null && String(raw).trim() !== "") {
            await this.applyReservationDeepLink(String(raw).trim());
        } else {
            this.fetchReservations();
        }
        emitter.on("ws-update", this.handleReservationWsUpdate);
    },
    beforeUnmount() {
        emitter.off("ws-update", this.handleReservationWsUpdate);
    },
    methods: {
        queryWithoutReservation() {
            const q = { ...this.$route.query };
            delete q.reservation;
            return q;
        },
        async applyReservationDeepLink(uid) {
            try {
                const { data } = await axios.get(
                    `${process.env.VUE_APP_API_URL}/reservations/${encodeURIComponent(uid)}/`
                );
                const formType = data.entry_summary?.form_type;
                if (formType === "ware" || formType === "truck") {
                    if (formType !== this.entryType) {
                        await this.$router.replace({
                            name: formType === "ware" ? "WareReservations" : "TruckReservations",
                            query: { ...this.$route.query, reservation: uid },
                        });
                        return;
                    }
                }
                const companyId = this.$store.state.auth.user?.company;
                this.activeTab =
                    companyId != null && data.reserved_by_company?.id === companyId
                        ? "contracted"
                        : "received";
                await this.$router.replace({
                    path: this.$route.path,
                    query: this.queryWithoutReservation(),
                });
                await this.fetchReservations();
            } catch (err) {
                console.error("Reservation deep link failed:", err);
                await this.$router.replace({
                    path: this.$route.path,
                    query: this.queryWithoutReservation(),
                });
                await this.fetchReservations();
            }
        },
        handleReservationWsUpdate(data) {
            if (data.type !== "reservation_update") return;
            const formType = data.reservation?.entry_summary?.form_type;
            const shouldRefreshList = !formType || formType === this.entryType;
            if (shouldRefreshList) {
                this.fetchReservations();
            }
            if (data.badge_affected) {
                emitter.emit("refresh-reservation-badge");
            }
        },
        async fetchReservations() {
            this.loading = true;
            try {
                if (this.activeTab === "contracted") {
                    await markReservationTabViewed(this.entryType, this.activeTab);
                }
                const [contracted, received] = await Promise.all([
                    axios.get(`${process.env.VUE_APP_API_URL}/reservations/?my=contracted&entry_type=${this.entryType}`),
                    axios.get(`${process.env.VUE_APP_API_URL}/reservations/?my=received&entry_type=${this.entryType}`),
                ]);
                this.contractedReservations = contracted.data;
                this.receivedReservations = received.data;
                emitter.emit("refresh-reservation-badge");
            } catch (err) {
                console.error("Failed to fetch reservations:", err);
                showAlertModal(this.$store, "Failed to load reservations", "danger", 5000);
            } finally {
                this.loading = false;
            }
        },
        async handleConfirm(reservation, driverInfo) {
            try {
                await axios.post(`${process.env.VUE_APP_API_URL}/reservations/${reservation.uid}/confirm/`, driverInfo || {});
                await this.fetchReservations();
                emitter.emit("refresh-reservation-badge");
            } catch (err) {
                showAlertModal(this.$store, err.response?.data?.error || "Failed to confirm", "danger", 5000);
            }
        },
        async handleDecline(reservation) {
            try {
                await axios.post(`${process.env.VUE_APP_API_URL}/reservations/${reservation.uid}/decline/`);
                await this.fetchReservations();
                emitter.emit("refresh-reservation-badge");
            } catch (err) {
                showAlertModal(this.$store, err.response?.data?.error || "Failed to decline", "danger", 5000);
            }
        },
        openCancelConfirm(reservation) {
            this.cancelReservationToConfirm = reservation;
        },
        closeCancelConfirm() {
            this.cancelReservationToConfirm = null;
        },
        async confirmCancelReservation() {
            const reservation = this.cancelReservationToConfirm;
            this.closeCancelConfirm();
            if (!reservation) return;
            try {
                await axios.post(`${process.env.VUE_APP_API_URL}/reservations/${reservation.uid}/cancel/`);
                await this.fetchReservations();
                emitter.emit("refresh-reservation-badge");
                showAlertModal(this.$store, this.$t("reservations.reservationCancelled"), "success", 3000);
            } catch (err) {
                showAlertModal(this.$store, err.response?.data?.error || "Failed to cancel", "danger", 5000);
            }
        },
        async handleGenerateTransportOrder(reservation) {
            try {
                const { data } = await axios.get(
                    `${process.env.VUE_APP_API_URL}/reservations/${reservation.uid}/transport-order/`,
                    { params: { lang: this.$i18n?.locale || "en" } }
                );
                this.transportOrderReservation = reservation;
                this.transportOrderFieldValues = data.field_values || {};
                this.transportOrderModalVisible = true;
            } catch (err) {
                showAlertModal(this.$store, err.response?.data?.error || "Failed to load transport order", "danger", 5000);
            }
        },
        async handleModifyTransportOrder(reservation) {
            await this.handleGenerateTransportOrder(reservation);
        },
        handleDownloadTransportOrder(reservation) {
            const url = reservation.transport_order_preview?.generated_pdf_url;
            if (url) window.open(url, "_blank");
        },
        async handleSendTransportOrder(reservation) {
            try {
                await axios.post(`${process.env.VUE_APP_API_URL}/reservations/${reservation.uid}/transport-order/send/`);
                const email = reservation.reserved_by_user?.email || "-";
                showAlertModal(this.$store, this.$t("reservations.transportOrderSentSuccessfullyTo", { email }), "success", 3000);
            } catch (err) {
                showAlertModal(this.$store, err.response?.data?.error || "Failed to send", "danger", 5000);
            }
        },
        async handleTransportOrderSave(payload) {
            if (!this.transportOrderReservation) return;
            const reservation = this.transportOrderReservation;
            let displayTimezone;
            try {
                displayTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
            } catch {
                displayTimezone = undefined;
            }
            const body = {
                transport_order_number: payload.transport_order_number,
                driver_name: payload.driver_name,
                driver_surname: payload.driver_surname,
                car_number: payload.car_number,
                notes: payload.observations || "",
                weight: payload.weight,
                volume: payload.volume,
                mdp: payload.mdp,
                price: payload.price,
                currency: payload.currency,
                vat_type: payload.vat_type,
                payment_term_code: payload.payment_term_code,
                distance_km: payload.distance_km,
                duration_text: payload.duration_text,
                pickup_address: payload.pickup_address || {},
                delivery_address: payload.delivery_address || {},
                display_timezone: displayTimezone,
                truck_special_features: payload.truck_special_features || [],
            };
            if (this.entryType === "ware") {
                body.platform_type = payload.platform_type || [];
            }
            if (this.entryType === "truck") {
                body.truck_type = payload.truck_type;
            }
            try {
                await axios.post(`${process.env.VUE_APP_API_URL}/reservations/${reservation.uid}/transport-order/`, body);
                this.transportOrderModalVisible = false;
                this.transportOrderReservation = null;
                this.transportOrderFieldValues = null;
                await this.fetchReservations();
                showAlertModal(this.$store, "Transport order saved.", "success", 3000);
            } catch (err) {
                showAlertModal(this.$store, err.response?.data?.error || "Failed to save transport order", "danger", 5000);
            }
        },
        async handleConfirmTransportOrder(reservation) {
            try {
                await axios.post(`${process.env.VUE_APP_API_URL}/reservations/${reservation.uid}/transport-order/confirm/`);
                await this.fetchReservations();
                showAlertModal(this.$store, "Transport order confirmed.", "success", 3000);
            } catch (err) {
                showAlertModal(this.$store, err.response?.data?.error || "Failed to confirm", "danger", 5000);
            }
        },
        async handleUploadSignedPdf(reservation, file) {
            const formData = new FormData();
            formData.append("signed_pdf", file);
            try {
                await axios.post(`${process.env.VUE_APP_API_URL}/reservations/${reservation.uid}/transport-order/upload-signed/`, formData, {
                    headers: { "Content-Type": "multipart/form-data" },
                });
                await this.fetchReservations();
                showAlertModal(this.$store, "Signed PDF uploaded.", "success", 3000);
            } catch (err) {
                showAlertModal(this.$store, err.response?.data?.error || "Failed to upload", "danger", 5000);
            }
        },
        async handleUploadCmr(reservation, file) {
            const formData = new FormData();
            formData.append("cmr_pdf", file);
            try {
                await axios.post(`${process.env.VUE_APP_API_URL}/reservations/${reservation.uid}/transport-order/upload-cmr/`, formData, {
                    headers: { "Content-Type": "multipart/form-data" },
                });
                await this.fetchReservations();
                showAlertModal(this.$store, this.$t("reservations.cmrUploaded"), "success", 3000);
            } catch (err) {
                showAlertModal(this.$store, err.response?.data?.error || "Failed to upload", "danger", 5000);
            }
        },
        async handleUploadInvoice(reservation, file) {
            const formData = new FormData();
            formData.append("invoice_pdf", file);
            try {
                await axios.post(`${process.env.VUE_APP_API_URL}/reservations/${reservation.uid}/transport-order/upload-invoice/`, formData, {
                    headers: { "Content-Type": "multipart/form-data" },
                });
                await this.fetchReservations();
                showAlertModal(this.$store, this.$t("reservations.invoiceUploaded"), "success", 3000);
            } catch (err) {
                showAlertModal(this.$store, err.response?.data?.error || "Failed to upload", "danger", 5000);
            }
        },
        async handleUploadGeneratedTransportOrder(reservation, file) {
            const formData = new FormData();
            formData.append("generated_pdf", file);
            try {
                await axios.post(
                    `${process.env.VUE_APP_API_URL}/reservations/${reservation.uid}/transport-order/upload-generated/`,
                    formData,
                    {
                        headers: { "Content-Type": "multipart/form-data" },
                    }
                );
                await this.fetchReservations();
                emitter.emit("refresh-reservation-badge");
                showAlertModal(this.$store, this.$t("reservations.transportOrderPdfUploaded"), "success", 3000);
            } catch (err) {
                showAlertModal(this.$store, err.response?.data?.error || "Failed to upload", "danger", 5000);
            }
        },
        async handleConfirmCompleted(reservation) {
            try {
                await axios.post(`${process.env.VUE_APP_API_URL}/reservations/${reservation.uid}/confirm-completed/`);
                await this.fetchReservations();
                showAlertModal(this.$store, "Completed confirmed.", "success", 3000);
            } catch (err) {
                showAlertModal(this.$store, err.response?.data?.error || "Failed to confirm", "danger", 5000);
            }
        },
        async handleRated(reservation) {
            await this.fetchReservations();
            const companyName =
                reservation.rated_company_id === reservation.reserved_by_company?.id
                    ? reservation.reserved_by_company?.name
                    : reservation.owner_company_name;
            const company = companyName || "—";
            showAlertModal(this.$store, this.$t("rating.submittedFor", { company }), "success", 3000);
        },
    },
};
</script>

<style scoped>
.reservations-tabs-wrapper {
    min-height: 0;
    overflow-x: hidden;
    display: flex;
    flex-direction: column;
}
.reservations-tabs-wrapper :deep(.v-tabs) {
    flex-shrink: 0;
}
.reservations-tabs-wrapper :deep(.v-tabs-window),
.reservations-tabs-wrapper :deep(.v-window) {
    flex: 1 1 0;
    min-height: 0;
    display: flex;
    flex-direction: column;
}
.reservations-tabs-wrapper :deep(.v-window__container) {
    flex: 1 1 0;
    min-height: 0;
    overflow-x: hidden;
    overflow-y: auto;
}
.reservation-info-content {
    max-height: 60vh;
    overflow-y: auto;
}
</style>
